/**
 * API Contract Types for Job Operations
 *
 * These types define the request/response contracts for job-related API endpoints.
 * All packages (api, web, mcp-server, cli) should import from here.
 */

import type {
  JobStatus,
  ExtractedResult,
  OutputSchema,
  DeviceClass,
  JobMetadata,
  JobAttachment,
  MultiSessionTranscription,
  ConversationMessage,
  NormalizedJobData,
  JobModifier,
} from '../job/index.js';
import type { PassStatus } from '../job/pass-status.js';
import type { UrlHostMismatch } from '../utils/url-host-mismatch.js';
import type {
  SessionEvents,
  KeyMoment,
  ConsoleMessage,
  NetworkRequest,
  PageError,
  PlaywrightEvent,
  PageStateSnapshot,
  TruncationInfo,
} from '../issue-testing/index.js';
import type { ExtractedIssueWithRelation } from '../github-issues/issue-extraction.types.js';
import type { CreatedIssueInfo, RecordingStatus } from '../job/index.js';
import type { TesterDevice, TesterLanguage } from '../tester/index.js';
import type { LiveKitRecordingResponse } from '../contracts/livekit.contract.js';
import type { SlackNotificationConfig } from '../slack-integration/index.js';
import type { EmailOnCompletionPref } from '../notifications/email-on-completion.js';

/**
 * Request to create a new test job
 */
export interface CreateTestJobRequest {
  /** Project ID (optional if githubRepo or organizationId is provided) */
  projectId?: string;
  /** Organization ID for PAT auth when no projectId is provided (enables project auto-creation from githubRepo) */
  organizationId?: string;
  /** URL to test (can be omitted if project has defaultUrl or template provides it) */
  url?: string;
  /** Test instructions (can be omitted if template provides testDescription) */
  description?: string;
  /** Template name to use as base configuration. Template fields are applied first, then direct fields override. */
  template?: string;
  /** Raw template content (markdown with YAML frontmatter). If provided, will be parsed server-side and merged with explicit fields. */
  templateContent?: string;
  /** Target duration for the test in minutes */
  targetDurationMinutes?: number;
  /** Overtime budget in minutes — total extra time the tester can use past the base duration (default: 15). Set to 0 to disable. */
  maxExtensionMinutes?: number;
  /** Schema defining structured data to extract from test results */
  outputSchema?: OutputSchema;
  /** MDForm template for text report - AI fills this to create results.text */
  resultsTemplate?: string;
  /** Device class for viewport and egress encoding ('desktop' | 'mobile') */
  deviceClass?: DeviceClass;
  /** @deprecated Use deviceClass instead. Legacy field accepted for backwards compatibility. */
  screenSize?: unknown;
  /** Media attachments (images, videos, documents) for additional context (max 10) */
  attachments?: JobAttachment[];
  /** Metadata for tracking job source, billing, and contextual information */
  metadata?: JobMetadata;
  /** Custom instructions for the LLM when validating tester results */
  additionalValidationInstructions?: string;
  /** GitHub repositories for LLM context (format: "owner/repo") — defaults to project repos */
  githubRepos?: string[];
  /** Single GitHub repo (format: "owner/repo"). Use githubRepos for multiple. */
  githubRepo?: string;
  /** Target repository for auto-created GitHub issues (format: "owner/repo") — must be one of githubRepos */
  autoCreateGithubIssuesRepo?: string;
  /** GitHub token from GitHub Actions to enable GitHub operations without requiring GitHub App installation */
  githubToken?: string;
  /**
   * Commit SHA the test is run against. When set and it differs from
   * the repo's base-index SHA, stage 5 (code context) builds a branch
   * overlay so code lookups reflect the PR-branch code instead of the
   * default branch. Optional; ad-hoc tests against deployed URLs leave
   * this undefined.
   */
  commitSha?: string;
  /**
   * Per-job opt-out for the code-context feature (#2892, #3369). Stage 5
   * (file/symbol refs, root cause, suggested fix) runs by default when
   * the job's org is on the Clerk allowlist. Set `false` to skip it on
   * this one job; non-allowlisted orgs never get code context regardless
   * of this flag.
   */
  enableCodeContext?: boolean;
  /** PR numbers to test (triggers AI-powered test plan consolidation) */
  prNumbers?: number[];
  /** Issue numbers to test (triggers AI-powered test plan consolidation) */
  issueNumbers?: number[];
  /** If true, reject job early if AI determines it's not testable. Default: true when prNumbers/issueNumbers provided */
  checkTestability?: boolean;
  // Tester pool requirements (Max/Enterprise/Enterprise Pro plans only)
  /** Required device capabilities for the tester */
  requiredDevices?: TesterDevice[];
  /** Required language capabilities for the tester */
  requiredLanguages?: TesterLanguage[];
  /** Whether the tester must be able to create social videos */
  requireSocialVideos?: boolean;
  /** Whether the tester must be in the sideload pool (Android APK or iOS IPA installer). When true, `requiredDevices` must contain exactly one of `'ios'` or `'android'`. */
  requireSideload?: boolean;
  /**
   * Triggers the Runhuman dual-device self-test flow (Device 1 runs the
   * production RunHuman app; Device 2 installs the dev APK under test and
   * screen-shares back). Internal-only — rejected for non-@volter.ai users
   * server-side. Implies `requireSideload: true` with `requiredDevices: ['android']`.
   */
  requiresRunhumanApkInstall?: boolean;
  /** Whether to automatically create GitHub issues from AI-extracted findings after job completion */
  autoCreateGithubIssues?: boolean;
  /** Whether to automatically create GitHub issues from the AI-extracted **feedback** bucket (#3483). */
  autoCreateGithubFeedback?: boolean;
  /** Whether to automatically create Jira issues from the AI-extracted **feedback** bucket (#3483). */
  autoCreateJiraFeedback?: boolean;
  /** Whether to automatically create Jira issues from AI-extracted findings after job completion (#5467). */
  autoCreateJiraIssues?: boolean;
  /** Whether to automatically create Linear issues from AI-extracted findings after job completion. */
  autoCreateLinearIssues?: boolean;
  /**
   * Whether to render an enhanced video output (logo bookends, burned-in
   * captions from the tester transcript, key-moment overlays for extracted
   * issues) in addition to the raw recording. Generated asynchronously by the
   * video-worker process group after job completion. Off by default — opt-in
   * because rendering is expensive and is intended for sharable artifacts (e.g.
   * Autonomy customer-facing summaries). See #3516.
   */
  enhancedVideo?: boolean;
  /**
   * Whether to rewrite the tester-facing instructions using the project's
   * Knowledge Base (features + user journeys) before posting. When `true`,
   * the job lands in `preparing`, the `kb_enhancement_worker` Fly process
   * group runs an agentic search-and-consolidate flow against the project's
   * KB, and the tester sees a feature-grounded plan instead of the bare
   * description. Enterprise+ orgs only; the project must have a successful
   * KB augmentation run. See #4353 + #4463.
   */
  enhanceInstructions?: boolean;
  /**
   * When auto-creation is enabled and this is `true`, skip auto-filing
   * `testerSurfaced: false` issues (inferred purely from telemetry or
   * mentioned by the tester only as a neutral observation). Opt-in; defaults
   * to filing all extracted issues. See #3160.
   */
  autoCreateOnlyTesterSurfaced?: boolean;
  /**
   * When a duplicate match resolves to a closed GitHub/Jira issue, reopen it.
   * Opt-in; defaults to leaving closed duplicates closed. See #3430.
   */
  reopenIssuesOnDuplicate?: boolean;
  /**
   * Post a cross-reference comment on the matched issue when a duplicate is
   * detected. Opt-in; defaults to NOT commenting so re-runs don't spam existing
   * tickets. See #5533.
   */
  commentOnDuplicate?: boolean;
  /** Slack notification config override for this specific job */
  slackNotification?: SlackNotificationConfig;
  /**
   * Per-job email-on-completion preference (tri-state). Stamped onto the
   * created job. Default `'inherit'` defers to the project's tier, then
   * to the user's global Job updates preference. See #3514.
   */
  emailOnCompletion?: EmailOnCompletionPref;
  /**
   * Per-job in-app-on-completion preference (tri-state). In-app inbox twin
   * of {@link emailOnCompletion}. Default `'inherit'`. See #4863.
   */
  inAppOnCompletion?: EmailOnCompletionPref;
  /**
   * Per-job email-on-failure preference (tri-state) for the new
   * "notify when job fails" path. Default `'inherit'`. See #4863.
   */
  emailOnFailure?: EmailOnCompletionPref;
  /**
   * Per-job in-app-on-failure preference (tri-state). In-app inbox twin of
   * {@link emailOnFailure}. Default `'inherit'`. See #4863.
   */
  inAppOnFailure?: EmailOnCompletionPref;
}

/**
 * Body for `POST /api/jobs/:jobId/rerun`.
 *
 * A rerun clones the source job's settings verbatim and re-dispatches them —
 * the workhorse for "run the identical test against a new build" after a rework
 * invalidates the previous video. Every field here **overrides** the value
 * copied from the source job, so an empty body (`{}`) reproduces the job
 * exactly while `{ url }` swaps only the target and `{ commitSha }` only the
 * build.
 *
 * `projectId` and `organizationId` are `Omit`ted because a rerun always
 * inherits the source job's project + org — they are set by the system, never
 * the caller. The system likewise always stamps `metadata.originalJobId` (the
 * lineage back to the source job); a caller-supplied `metadata` merges *under*
 * it. `metadata.source` stays caller-declared, matching the create endpoint, so
 * each surface (dashboard, CLI, …) keeps its own attribution.
 */
export type RerunTestJobRequest = Partial<Omit<CreateTestJobRequest, 'projectId' | 'organizationId'>>;

/**
 * Advisory returned on job creation when the dispatched URL's host diverges
 * from the resolved project's `defaultUrl` host (#6009). The server detects it
 * (it alone sees the resolved URL for every source — positional, template, or
 * project default); the CLI presents it and picks severity from whether the
 * project was explicit (`-p`) or defaulted. `projectName` rides along so the
 * CLI can describe the project without a second fetch.
 */
export interface ProjectUrlMismatch extends UrlHostMismatch {
  readonly projectName: string;
}

/**
 * Result of testability check performed during job creation
 */
export interface TestabilityResult {
  /** Whether the job is testable */
  isTestable: boolean;
  /** Reason if not testable */
  reason?: string;
  /** Confidence score 0-1 */
  confidence: number;
}

/**
 * Response after creating a test job (async mode)
 */
export interface CreateTestJobResponse {
  /** Unique identifier for the created job */
  jobId: string;
  /** Human-readable confirmation message */
  message: string;
  /** Testability result (only present when checkTestability is true) */
  testability?: TestabilityResult;
  /** Deprecation warnings about fields that will be removed in future versions */
  deprecationWarnings?: string[];
  /** Non-fatal configuration warnings distinct from deprecations. Reserved as
   * an extension point — fatal config problems are rejected with 400 instead. */
  warnings?: string[];
  /** Present when the dispatched URL's host diverges from the resolved
   * project's default-URL host (#6009). Advisory only — never blocks creation. */
  projectUrlMismatch?: ProjectUrlMismatch;
}

/**
 * Response after creating a test job (sync mode - waits for completion)
 */
export interface CreateTestJobSyncResponse {
  /** Unique identifier for the job */
  id: string;
  /** Current job status */
  status: JobStatus;
  /** Extracted test results (if completed successfully) */
  result?: ExtractedResult;
  /** Error message (if failed) */
  error?: string;
  /** Human-readable confirmation message */
  message: string;
  /** Raw response text from the tester */
  testerResponse?: string;
  /** Anonymous alias for the assigned tester (e.g., "Phoenix") */
  testerAlias?: string;
  /** Avatar URL for the assigned tester */
  testerAvatarUrl?: string;
  /** Deprecation warnings about fields that will be removed in future versions */
  deprecationWarnings?: string[];
  /** Non-fatal configuration warnings emitted at job creation. Reserved as an
   * extension point — fatal config problems are rejected with 400 instead. */
  warnings?: string[];
}

/**
 * Response with full job status and results.
 *
 * The outcome is the (`status`, `modifier`, `passStatus`) triple — `status` for
 * the lifecycle state, `modifier` for a terminal-failure reason, `passStatus`
 * for the AI verdict on a `completed` job. Read the triple, not `passStatus`
 * alone; a null `passStatus` is not "still pending". See the outcome-bucket
 * mapping in `docs/job-result-taxonomy.md` and the canonical definitions in
 * `job/job-status.ts` + `job/pass-status.ts`.
 */
export interface JobStatusResponse {
  /** Unique identifier for the job */
  id: string;
  /** Test target URL (the page under test) */
  url?: string;
  /** Original test description supplied by the caller */
  description?: string;
  /** AI-generated detailed test plan (used by tester/AI when present) */
  augmentedDescription?: string;
  /** Current job status */
  status: JobStatus;
  /** Granular reason within `status`. See `JobModifier`. */
  modifier?: JobModifier | null;
  /** Extracted test results (if completed successfully) */
  result?: ExtractedResult;
  /** Error message (if failed) */
  error?: string;
  /** Timestamp when tester claimed the job */
  claimedAt?: string;
  /** Timestamp when job completed */
  completedAt?: string;
  /** Actual test duration in seconds */
  testDurationSeconds?: number;
  /** Amount charged to the client in USD (not tester compensation — that is handled out-of-band) */
  costUsd?: number;
  /** Playwright session data (screenshots, video, console logs, network requests) */
  testerData?: SessionEvents;
  /** Raw response text from the tester */
  testerResponse?: string;
  /** Anonymous alias for the assigned tester (e.g., "Phoenix") */
  testerAlias?: string;
  /** Avatar URL for the assigned tester */
  testerAvatarUrl?: string;
  /** Full URL to view job details on the dashboard */
  jobUrl?: string;
  /** Name of the project this job belongs to */
  projectName?: string;
  /** Target duration for the test in minutes */
  targetDurationMinutes?: number;
  /** Overtime budget in minutes — total extra time on top of base duration. */
  maxExtensionMinutes?: number;
  /** Deadline for the tester to respond (ISO string). Stable invariant: claim + (target + maxExtension). */
  responseDeadline?: string;
  /** Key moments from test execution timeline with optional screenshots */
  keyMoments?: KeyMoment[];
  /** Voice transcription session with word-level timestamps */
  transcription?: MultiSessionTranscription;
  /** Conversation history between tester and AI */
  conversationHistory?: ConversationMessage[];
  /** Whether rich results (video, screenshots, transcript) are gated behind a paid plan */
  richResultsGated?: boolean;
  /** Display name of the owning organization. Populated only when `richResultsGated` is true (Issue #2589). */
  ownerOrganizationName?: string;
  /** True iff the viewer is the owner of the owning org. Populated only when `richResultsGated` is true (Issue #2589). */
  viewerIsOrganizationOwner?: boolean;
  /** Device class for viewport and egress encoding ('desktop' | 'mobile') */
  deviceClass?: DeviceClass;
  /** Required device capabilities for the tester (Max/Enterprise plans) */
  requiredDevices?: TesterDevice[];
  /** Required language capabilities for the tester (Max/Enterprise plans) */
  requiredLanguages?: TesterLanguage[];
  /** Whether the tester must be able to create social videos (Max/Enterprise plans) */
  requireSocialVideos?: boolean;
  /** Whether the tester must be in the sideload pool — Android APK or iOS IPA installer (Max/Enterprise plans) */
  requireSideload?: boolean;
  /** True for Runhuman dual-device self-test jobs (internal-only) */
  requiresRunhumanApkInstall?: boolean;
  /** AI-extracted potential issues from test results, enriched with related issue detection */
  extractedIssues?: ExtractedIssueWithRelation[];
  /** GitHub issues created from AI-extracted findings */
  createdIssues?: CreatedIssueInfo[];
  /** Pre-computed, display-ready normalized data with video-relative offsets (Issue #2175) */
  normalizedData?: NormalizedJobData;
  /** AI-determined Pass/Fail/Not-Applicable verdict (#4113). Always populated on completed jobs. */
  passStatus?: PassStatus | null;
  /**
   * Availability of the screen recording, computed without minting a presigned
   * URL. A poller watches this flip to `ready`, then fetches the recording via
   * `GET /jobs/:jobId/artifacts/recording` for the download URL(s) (#5943).
   */
  recordingStatus?: RecordingStatus;
}

/**
 * Valid artifact types for the GET /jobs/:jobId/artifacts/:artifactType endpoint.
 */
export const JOB_ARTIFACT_TYPES = [
  'structured-output',
  'transcript',
  'console-logs',
  'network-requests',
  'conversation',
  'events',
  'page-states',
  'key-moments',
  'recording',
] as const;

export type JobArtifactType = typeof JOB_ARTIFACT_TYPES[number];

/** Response map: each artifact type returns a specific shape */
export interface JobArtifactResponses {
  'structured-output': { result: ExtractedResult };
  'transcript': { transcription: MultiSessionTranscription };
  'console-logs': { consoleMessages: ConsoleMessage[]; truncationInfo?: TruncationInfo };
  'network-requests': { networkRequests: NetworkRequest[]; truncationInfo?: TruncationInfo };
  'conversation': { conversationHistory: ConversationMessage[] };
  'events': { events: PlaywrightEvent[]; errors: PageError[] };
  'page-states': { pageStates: PageStateSnapshot[] };
  'key-moments': { keyMoments: KeyMoment[] };
  'recording': LiveKitRecordingResponse;
}
